import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Flex,
  Checkbox,
  Input,
  Tag,
  TagLabel,
  VStack,
  HStack,
  Divider,
  Spinner,
  RadioGroup,
  Radio,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { Loading } from "./Loading";

const API_URL = import.meta.env.VITE_API_URL;

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value._id || value.id || value.roleId || value.userId || "");
};

const hasPermission = (entry, permission) => {
  if (!entry) return false;

  const accessList = Array.isArray(entry.access) ? entry.access : [];
  if (permission === "view") {
    return entry.canView === true || entry.view === true || accessList.includes("view");
  }

  return entry.canEdit === true || entry.edit === true || accessList.includes("edit");
};

const getRoleReportPermission = (role, reportId) => {
  if (!role?.reportAccess || !Array.isArray(role.reportAccess)) {
    return { view: false, edit: false };
  }

  const reportPermission = role.reportAccess.find(
    (r) => String(r.reportId) === String(reportId)
  );

  if (!reportPermission) {
    return { view: false, edit: false };
  }

  return {
    view: reportPermission.access?.includes("view") || false,
    edit: reportPermission.access?.includes("edit") || false,
  };
};


const hasReportsAccess = (role) => {
  const sections = Array.isArray(role?.roleAccess) ? role.roleAccess : [];

  return sections.some((section) => {
    const heading = (section?.heading || "").toLowerCase();

    // Check if heading itself is "reports"
    if (heading === "reports") return true;

    // Check all types within this section for "reports" subHeading
    const types = Array.isArray(section?.types) ? section.types : [];
    return types.some((type) => {
      const subHeading = (type?.subHeading || "").toLowerCase();
      const access = Array.isArray(type?.access) ? type.access : [];
      return subHeading === "reports" && access.length > 0;
    });
  });
};

const ShareReportModal = ({
  isOpen,
  onClose,
  report,
  reportName,
  selectedTables = [],
  selectedColumns = [],
  onShare,
}) => {
  const toast = useToast();
  const [accessType, setAccessType] = useState("private");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  const existingUserAccess = reportData?.accessControl?.userAccess || [];
  const existingRoleAccess = reportData?.accessControl?.roleAccess || [];

  useEffect(() => {
    if (!isOpen || !report?._id) return;

    const fetchReport = async () => {
      try {
        setLoadingReport(true);
        const token = localStorage.getItem("prima_session");

        const res = await axios.get(`${API_URL}/report/${report._id}?_t=${Date.now()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setReportData(res.data.data);
      } catch (err) {
        console.error("Failed to fetch report:", err);
      } finally {
        setLoadingReport(false);
      }
    };

    fetchReport();
  }, [isOpen, report]);

  useEffect(() => {
    if (!reportData) return;

    if (reportData.accessControl?.publicAccess) {
      setAccessType("public");
    } else if (reportData.accessControl?.privateAccess) {
      setAccessType("private");
    } else {
      setAccessType("shared");
    }
  }, [reportData]);
  useEffect(() => {
    if (!isOpen || accessType !== "shared") return;

    const fetchValidAccessData = async () => {
      try {
        setLoadingAccess(true);
        const token = localStorage.getItem("prima_session");
        const cleanToken = token?.replace(/^Bearer\s+/i, "").replace(/['"]/g, "") || "";

        // Fetch all roles with report access
        const rolesResponse = await axios.get(`${API_URL}/roles/all`, {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
        });

        const allRoles = rolesResponse?.data?.data || [];
        const rolesWithReportAccess = allRoles.filter(hasReportsAccess);

        console.log("Total roles fetched:", allRoles.length);
        console.log("Roles with report access:", rolesWithReportAccess.length);

        const rolesMapped = rolesWithReportAccess.map((role) => {
          const roleId = normalizeId(role?._id || role?.id || role?.roleId);
          const existing = existingRoleAccess.find(
            (r) => normalizeId(r?.roleId || r?.id) === roleId
          );
          const roleReportPermission = getRoleReportPermission(role, report?._id);

          return {
            id: roleId,
            name: role.roleName || role.name,
            view: hasPermission(existing, "view") || roleReportPermission.view,
            edit: hasPermission(existing, "edit") || roleReportPermission.edit,
          };
        });

        setRoles(rolesMapped);

        // Fetch all admins using direct API call
        const adminsResponse = await axios.get(`${API_URL}/admin/list`, {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
          params: {
            page: 1,
            limit: 10000,
          },
        });

        const allAdmins = adminsResponse?.data?.data || [];

        console.log("Total admins fetched:", allAdmins.length);
        console.log("Sample admin:", allAdmins[0]);

        // Create set of valid role IDs (handle both string and object roleId)
        const validRoleIds = new Set(
          rolesWithReportAccess.map((role) => String(role._id || role.id))
        );

        console.log("Valid role IDs with report access:", Array.from(validRoleIds));

        // Filter admins whose role has report access
        const adminsWithReportAccess = allAdmins.filter((admin) => {
          // Handle different roleId formats
          const roleIdValue = admin?.roleId?._id || admin?.roleId?.id || admin?.roleId;
          return validRoleIds.has(String(roleIdValue));
        });

        console.log("Admins with report access:", adminsWithReportAccess.length);

        const normalizedUsers = adminsWithReportAccess.map((admin) => {
          const adminId = normalizeId(admin?._id || admin?.id || admin?.userId || admin?.adminId);
          const existing = existingUserAccess.find(
            (u) => normalizeId(u?.userId || u?.id) === adminId
          );
          const userReportAccessEntry = Array.isArray(admin?.reportAccess)
            ? admin.reportAccess.find((entry) => normalizeId(entry?.reportId) === normalizeId(report?._id))
            : null;

          return {
            id: adminId,
            name: admin.name || admin.email,
            roleId: normalizeId(admin?.roleId),
            view: hasPermission(existing, "view") || hasPermission(userReportAccessEntry, "view"),
            edit: hasPermission(existing, "edit") || hasPermission(userReportAccessEntry, "edit"),
          };
        });
        const dedupedUsers = Array.from(
          new Map(normalizedUsers.map((user) => [user.id, user])).values()
        );

        setUsers(dedupedUsers);
      } catch (error) {
        console.error("Error fetching access data:", error);
        setRoles([]);
        setUsers([]);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchValidAccessData();
  }, [isOpen, accessType, reportData]);

  const hierarchy = useMemo(() => {
    const grouped = {};

    selectedColumns.forEach((column) => {
      const path = column?.path || "";
      const [tableFromPath, ...rest] = path.split(".");
      const table = selectedTables.includes(tableFromPath)
        ? tableFromPath
        : selectedTables[0] || tableFromPath || "General";

      if (!grouped[table]) grouped[table] = [];

      const formattedPath = rest.length ? rest.join(".") : path;
      grouped[table].push({
        label: column?.label || formattedPath,
        path: formattedPath,
      });
    });

    return grouped;
  }, [selectedColumns, selectedTables]);

  const handleUserPermChange = (userId, key, checked) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === userId ? { ...user, [key]: checked } : user))
    );
  };

  const handleRolePermChange = (roleId, key, checked) => {
    setRoles((prev) =>
      prev.map((role) => (role.id === roleId ? { ...role, [key]: checked } : role))
    );

    setUsers((prev) =>
      prev.map((user) =>
        user.roleId === String(roleId) ? { ...user, [key]: checked } : user
      )
    );
  };

  const handleShare = async () => {
    const selectedUserAccess = users
      .filter((user) => user.view || user.edit)
      .map((user) => ({
        userId: user.id,
        name: user.name,
        canView: user.view,
        canEdit: user.edit,
      }));

    const selectedRoleAccess = roles
      .filter((role) => role.view || role.edit)
      .map((role) => ({
        roleId: role.id,
        name: role.name,
        canView: role.view,
        canEdit: role.edit,
      }));

    // Validate: if Shared is selected, at least one role or user must have permissions
    if (accessType === "shared" && selectedUserAccess.length === 0 && selectedRoleAccess.length === 0) {
      toast({
        title: "No access granted",
        description: "Please select at least one role or user, or choose Public/Private access.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      publicAccess: accessType === "public",
      privateAccess: accessType === "private",
      userAccess: accessType === "shared" ? selectedUserAccess : [],
      roleAccess: accessType === "shared" ? selectedRoleAccess : [],
    };

    console.log("Share payload:", payload);

    if (onShare) {
      await Promise.resolve(onShare(payload));
    }

    // Keep checkboxes sticky in UI after share, even before next refetch.
    setReportData((prev) => ({
      ...(prev || {}),
      accessControl: {
        ...(prev?.accessControl || {}),
        publicAccess: payload.publicAccess,
        privateAccess: payload.privateAccess,
        userAccess: payload.userAccess,
        roleAccess: payload.roleAccess,
      },
    }));

    if (accessType === "shared") {
      setRoles((prev) =>
        prev.map((role) => {
          const selected = payload.roleAccess.find(
            (entry) => normalizeId(entry.roleId) === normalizeId(role.id)
          );
          return {
            ...role,
            view: selected ? !!selected.canView : false,
            edit: selected ? !!selected.canEdit : false,
          };
        })
      );

      setUsers((prev) =>
        prev.map((user) => {
          const selected = payload.userAccess.find(
            (entry) => normalizeId(entry.userId) === normalizeId(user.id)
          );
          return {
            ...user,
            view: selected ? !!selected.canView : false,
            edit: selected ? !!selected.canEdit : false,
          };
        })
      );
    }

    onClose();
  };

  const visibleUsers = users.filter((user) => {
    if (!userSearch.trim()) return true;
    const query = userSearch.toLowerCase();
    return user.name.toLowerCase().includes(query);
  });

  const visibleRoles = roles.filter((role) => {
    if (!roleSearch.trim()) return true;
    return role.name.toLowerCase().includes(roleSearch.toLowerCase());
  });

  if (isOpen && (loadingReport || (accessType === "shared" && loadingAccess))) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent borderRadius="16px">
          <ModalHeader color="#303A6D">Share {reportName || "Report"}</ModalHeader>
          <ModalBody>
            <Loading />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="16px">
        <ModalHeader color="#303A6D">Share {reportName || "Report"}</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Flex gap={10}>
              <RadioGroup value={accessType} onChange={setAccessType}>
                <HStack spacing={10} align="start">
                  <Box>
                    <Text fontWeight="600" mb={2}>Public</Text>
                    <Radio value="public">Anyone can access</Radio>
                  </Box>
                  <Box>
                    <Text fontWeight="600" mb={2}>Shared</Text>
                    <Radio value="shared">Selected roles/users can access</Radio>
                  </Box>
                  <Box>
                    <Text fontWeight="600" mb={2}>Private</Text>
                    <Radio value="private">Only you can access</Radio>
                  </Box>
                </HStack>
              </RadioGroup>
            </Flex>

            <Divider />

            <Box>
              <Text fontWeight="700" color="#303A6D" mb={2}>Data Hierarchy</Text>
              <VStack align="stretch" spacing={3} maxH="160px" overflowY="auto">
                {Object.keys(hierarchy).length === 0 ? (
                  <Text fontSize="sm" color="gray.500">No tables/columns selected yet.</Text>
                ) : (
                  Object.entries(hierarchy).map(([table, columns]) => (
                    <Box key={table}>
                      <HStack mb={2}>
                        <Tag colorScheme="blue" size="sm">
                          <TagLabel>{table}</TagLabel>
                        </Tag>
                      </HStack>
                      <Flex wrap="wrap" gap={2}>
                        {columns.map((column) => (
                          <Tag key={`${table}-${column.path}`} size="sm" variant="subtle" colorScheme="purple">
                            <TagLabel>{column.label}</TagLabel>
                          </Tag>
                        ))}
                      </Flex>
                    </Box>
                  ))
                )}
              </VStack>
            </Box>

            {accessType === "shared" && (
              <>
                <Divider />
                <Box>
                  <Text fontWeight="700" color="#303A6D" mb={2}>Role-based Access</Text>
                  <Input
                    size="sm"
                    placeholder="Search roles"
                    mb={2}
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                  />
                  {loadingAccess ? (
                    <Flex justify="center" py={3}><Spinner size="sm" /></Flex>
                  ) : visibleRoles.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">No child roles with report access found.</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {visibleRoles.map((role) => (
                        <Flex key={role.id} align="center" justify="space-between" gap={4}>
                          <Text fontSize="sm">{role.name}</Text>
                          <HStack spacing={5}>
                            <Checkbox
                              isChecked={role.view}
                              onChange={(e) => handleRolePermChange(role.id, "view", e.target.checked)}
                            >
                              View
                            </Checkbox>
                            <Checkbox
                              isChecked={role.edit}
                              onChange={(e) => handleRolePermChange(role.id, "edit", e.target.checked)}
                            >
                              Edit
                            </Checkbox>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </Box>

                     <Divider />
                     
                <Box>
                  <Text fontWeight="700" color="#303A6D" mb={2}>User-based Access</Text>
                  <Input
                    size="sm"
                    placeholder="Search users by name"
                    mb={2}
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                  {loadingAccess ? (
                    <Flex justify="center" py={3}><Spinner size="sm" /></Flex>
                  ) : visibleUsers.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">No admins found with report access.</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {visibleUsers.map((user) => (
                        <Flex key={user.id} align="center" justify="space-between" gap={4}>
                          <Text fontSize="sm">{user.name}</Text>
                          <HStack spacing={5}>
                            <Checkbox
                              isChecked={user.view}
                              onChange={(e) => handleUserPermChange(user.id, "view", e.target.checked)}
                            >
                              View
                            </Checkbox>
                            <Checkbox
                              isChecked={user.edit}
                              onChange={(e) => handleUserPermChange(user.id, "edit", e.target.checked)}
                            >
                              Edit
                            </Checkbox>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </Box>

               

                
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleShare}>Share</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareReportModal;
